import React, { useEffect, useMemo, useState } from 'react';
import { GridContainer, GridItem, InformationField } from '../../../components/shared';
import { Comment } from '@mui/icons-material';
import { Typography, IconButton, useMediaQuery, useTheme, Divider, Box } from '@mui/material';
import ChildField from '../AddCollection/ChildField';
import {
  FIELD_NAME,
  METER_TYPE,
  METER_TYPE_FILED_MAP,
  CREDIT_CARD_FEED_MODEM_TYPES,
} from '../../../constants/MeterTypeFieldMap';
import { MESSAGE } from '../../../constants/message';
import { CURRENCY_CONSTANT } from '../../../constants/CurrencyConstants';
import {
  getVisibleCollectionAssetMeters,
  getSummaryContent,
  sortAssetMeters,
  updateMeterNameForReconciliation,
} from '../../../utils/collection-methods';
import useStyles from '../style';
import SublocationSummaryField from './SublocationSummaryField';
import IntercardFields from '../AddCollection/IntercardFields';
import { getMeterLabel } from '../../../utils/collection-methods';
import { useSelector } from 'react-redux';
import { CommentBox } from '../../../components/CommentBox';
import { useFormikContext } from 'formik';
import { isArrayWithLength } from '../../../utils/common-methods';
import Modal from '../../../components/shared/Modal/Modal';
import ReportMachineModelBody from '../AddCollection/ReportMachineModelBody';
import ErrorEndAdornment from '../../../components/ErrorEndAdornment';

const ReconciliationMachineFormAccordionContent = ({ asset, skip, isStandaloneSublocation = true }) => {
  const { intercardTransactions } = asset;
  const visibleAssetMeters = useMemo(() => getVisibleCollectionAssetMeters(asset?.assetMeters || []), [asset]);
  const { values, setFieldValue } = useFormikContext();
  const theme = useTheme();
  const classes = useStyles();
  const isBelowSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isCompact = useMediaQuery(theme.breakpoints.down('xl'));
  const { subTotalRevenues, locationCurrency, reRender } = useSelector((state) => state.reconciliationCollection);

  const maxFieldCount = 9;
  const hasIntercardRows = isArrayWithLength(intercardTransactions);

  const getVisibleFieldCount = (assetMeter, meterName) => {
    const fields = METER_TYPE_FILED_MAP[meterName] || [];
    const abbreviation = locationCurrency?.abbreviation || CURRENCY_CONSTANT.USD;

    return fields.filter((el) => {
      const isCreditAdjustOrTestField = [FIELD_NAME.METER_ADJUST, FIELD_NAME.TEST_VENDS].includes(el.name);
      const isCreditAdjustField = el.name === FIELD_NAME.METER_ADJUST;
      const isCreditMeter = meterName === METER_TYPE.CREDIT_RECONCILIATION;
      const modemType = assetMeter?.modem?.type;
      const isCreditCardFeedMeter = CREDIT_CARD_FEED_MODEM_TYPES.includes(modemType);

      if (isCreditAdjustOrTestField && isCreditCardFeedMeter) {
        return false;
      }

      if (isCreditAdjustField && isCreditMeter && !isStandaloneSublocation) {
        return false;
      }

      if (abbreviation === CURRENCY_CONSTANT.USD && el.name === FIELD_NAME.REVENUE_IN_USD) {
        return false;
      }

      return true;
    }).length;
  };

  const sortedAssetMeters = useMemo(() => {
    const rawData = [...visibleAssetMeters];
    const sortedByExistingLogic = sortAssetMeters(rawData);

    return sortedByExistingLogic.sort((a, b) => {
      const meterNameA = updateMeterNameForReconciliation(a?.meter?.name, true);
      const meterNameB = updateMeterNameForReconciliation(b?.meter?.name, true);
      const countA = getVisibleFieldCount(a, meterNameA);
      const countB = getVisibleFieldCount(b, meterNameB);

      if (countB !== countA) return countB - countA;
      return (meterNameA || '').localeCompare(meterNameB || '');
    });
  }, [visibleAssetMeters, locationCurrency?.abbreviation, reRender]);

  const intercardFieldCount = 3;
  const metersAboveIntercard = sortedAssetMeters.filter((assetMeter) => {
    const meterName = updateMeterNameForReconciliation(assetMeter?.meter?.name, true);
    return getVisibleFieldCount(assetMeter, meterName) > intercardFieldCount;
  });
  const metersBelowOrEqualIntercard = sortedAssetMeters.filter((assetMeter) => {
    const meterName = updateMeterNameForReconciliation(assetMeter?.meter?.name, true);
    return getVisibleFieldCount(assetMeter, meterName) <= intercardFieldCount;
  });

  const getDynamicGridSx = (visibleFieldCount) => {
    const safeCount = Math.max(visibleFieldCount, 1);

    return {
      display: 'grid',
      gridTemplateColumns: `repeat(${safeCount}, minmax(95px, 1fr))`,
      gridAutoFlow: 'column',
      gridAutoColumns: 'minmax(95px, 1fr)',
    };
  };

  const renderMeterRows = (meters) =>
    meters?.map((assetMeter, index) => {
      const { meter } = assetMeter;

      const meterLabel = getMeterLabel(assetMeter, locationCurrency);
      let meterName = meter?.name;
      meterName = updateMeterNameForReconciliation(meterName, true);
      const visibleFieldCount = getVisibleFieldCount(assetMeter, meterName);
      const showWarning = assetMeter?.warnings?.length > 0 ? true : false;
      return (
        <GridItem sm={12} md={12} px={0} pt={0.25} key={`${meter?.id || meterName || index}-${index}`}>
          <Box className={classes.compactMeterRow}>
            <Box className={classes.compactMeterLabelWrap}>
              <Typography variant="subtitle4" className={classes.compactMeterLabel}>
                {meterLabel}
              </Typography>
              {showWarning && (
                <ErrorEndAdornment
                  message={assetMeter?.warnings.map((warning, warningIndex) => (
                    <React.Fragment key={warningIndex}>
                      {warning}
                      <br />
                    </React.Fragment>
                  ))}
                />
              )}
            </Box>
            <Box key={meter?.id} className={classes.compactMeterGrid} sx={getDynamicGridSx(visibleFieldCount)}>
              <ChildField
                assetId={asset?.id}
                assetMeterId={assetMeter?.id}
                assetMeter={assetMeter}
                sublocationId={asset?.subLocationId}
                meterName={meter?.name}
                meterId={meter?.id}
                fields={METER_TYPE_FILED_MAP[meterName]}
                costPerPlay={asset[FIELD_NAME.COST_PER_PLAY]}
                clicksPerPlay={asset[FIELD_NAME.CLICKS_PER_PLAY]}
                isReconciliation
                isCompactView
                skip={skip}
                maxFieldCount={maxFieldCount}
                isStandaloneSublocation={isStandaloneSublocation}
              />
            </Box>
          </Box>
        </GridItem>
      );
    });

  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [isIssueReported, setIsIssueReported] = useState(false);

  useEffect(() => {
    if (values) {
      const fieldName = `report_${asset.subLocationId}_${asset.id}`;
      const reportedValueFromFormikContext = values[fieldName];
      setIsIssueReported(reportedValueFromFormikContext?.status);
    }
  }, [asset, showReportModal, reRender]);

  const openCommentHandler = () => {
    setShowCommentModal(true);
  };

  const content = useMemo(
    () => getSummaryContent(subTotalRevenues, asset?.subLocationId, asset?.id, locationCurrency),
    [subTotalRevenues, asset],
  );

  const closeCommentBoxHandler = (commentData) => {
    if (values[`comment_${asset.subLocationId}_${asset.id}`]?.length !== commentData?.length) {
      setFieldValue(`comment_${asset.subLocationId}_${asset.id}`, commentData);
    }
    setShowCommentModal(false);
  };

  const submitCommentHandler = (v) => {
    setFieldValue(`comment_${asset.subLocationId}_${asset.id}`, v);
  };

  const onResolveReportedIssueHandler = (v) => {
    setFieldValue(`report_${asset.subLocationId}_${asset.id}`, v);
    setShowReportModal(false);
  };
  return (
    <React.Fragment>
      {visibleAssetMeters && visibleAssetMeters.length === 0 && (
        <InformationField my={1} pl={2} variant="subtitle1" value={MESSAGE.COLLECTION_NO_METERS || ''} />
      )}
      {(visibleAssetMeters?.length > 0 || hasIntercardRows) && (
        <React.Fragment>
          <Box className={classes.reconciliationContentLayout} sx={{ flexDirection: isCompact ? 'column' : 'row' }}>
            <Box className={classes.reconciliationGridSection}>
              <GridContainer pb={0} id={asset?.id} className={classes.compactMetersContainer}>
                {renderMeterRows(metersAboveIntercard)}
                {hasIntercardRows && (
                  <IntercardFields
                    intercardTransactions={intercardTransactions}
                    assetId={asset?.id}
                    sublocationId={asset?.subLocationId}
                    maxFieldCount={4}
                  />
                )}
                {renderMeterRows(metersBelowOrEqualIntercard)}
              </GridContainer>
              <Divider />
            </Box>

            <Box
              className={classes.reconciliationSummaryPanel}
              sx={isCompact ? { width: '100%', maxWidth: '100%', minWidth: 0, position: 'static' } : {}}
            >
              <Box className={classes.reconciliationSummaryActionRow}>
                <IconButton
                  px={1}
                  className={classes.comment}
                  onClick={openCommentHandler}
                  sx={{
                    color:
                      showCommentModal ||
                      (values[`comment_${asset.subLocationId}_${asset.id}`] &&
                        values[`comment_${asset.subLocationId}_${asset.id}`]?.length > 0)
                        ? theme.palette.primary.main
                        : theme.palette.secondary.main,
                  }}
                >
                  <Comment />
                </IconButton>
              </Box>
              {showCommentModal && (
                <CommentBox
                  handleClose={closeCommentBoxHandler}
                  handleSubmit={submitCommentHandler}
                  initialCommentData={values[`comment_${asset.subLocationId}_${asset.id}`]}
                  data={{
                    titleVariant: 'h3',
                    title: MESSAGE.RECONCILIATION_COMMENT_TITLE,
                    fieldName: `comment_${asset.subLocationId}_${asset.id}`,
                  }}
                />
              )}
              <Box mt={isBelowSm ? 1 : 0}>
                {isArrayWithLength(content) && <SublocationSummaryField content={content} isCompact={isCompact} />}
              </Box>
            </Box>
          </Box>
          <Modal
            title={MESSAGE.REPORT_MACHINE_BTN_NAME}
            open={showReportModal}
            onClose={() => setShowReportModal(false)}
            closeIcon={true}
          >
            <ReportMachineModelBody
              assetId={asset?.id}
              assetName={asset?.title}
              asset={asset}
              isReconciliation={true}
              onClose={() => setShowReportModal(false)}
              reportedIssue={setIsIssueReported}
              isIssueReported={isIssueReported}
              onConfirm={onResolveReportedIssueHandler}
            />
          </Modal>
        </React.Fragment>
      )}
      {(!visibleAssetMeters || visibleAssetMeters?.length === 0) && !hasIntercardRows && asset?.noCollectionReason && (
        <>
          <Box className={classes.reconciliationContentLayout} sx={{ flexDirection: isCompact ? 'column' : 'row' }}>
            <Box className={classes.reconciliationGridSection} />
            <Box
              className={classes.reconciliationSummaryPanel}
              sx={isCompact ? { width: '100%', maxWidth: '100%', minWidth: 0, position: 'static' } : {}}
            >
              <Box className={classes.reconciliationSummaryActionRow}>
                <IconButton
                  px={1}
                  className={classes.comment}
                  onClick={openCommentHandler}
                  sx={{
                    color: showCommentModal ? theme.palette.primary.main : theme.palette.secondary.main,
                  }}
                >
                  <Comment />
                </IconButton>
              </Box>
              {showCommentModal && (
                <CommentBox
                  handleClose={closeCommentBoxHandler}
                  handleSubmit={submitCommentHandler}
                  initialCommentData={values[`comment_${asset.subLocationId}_${asset.id}`]}
                  data={{
                    titleVariant: 'h3',
                    title: MESSAGE.RECONCILIATION_COMMENT_TITLE,
                    fieldName: `comment_${asset.subLocationId}_${asset.id}`,
                  }}
                />
              )}
              <Box mt={isBelowSm ? 1 : 0}>
                {isArrayWithLength(content) && <SublocationSummaryField content={content} isCompact={isCompact} />}
              </Box>
            </Box>
          </Box>
          <Modal
            title={MESSAGE.REPORT_MACHINE_BTN_NAME}
            open={showReportModal}
            onClose={() => setShowReportModal(false)}
            closeIcon={true}
          >
            <ReportMachineModelBody
              assetId={asset?.id}
              assetName={asset?.title}
              asset={asset}
              isReconciliation={true}
              onClose={() => setShowReportModal(false)}
              onConfirm={onResolveReportedIssueHandler}
              reportedIssue={setIsIssueReported}
              isIssueReported={isIssueReported}
            />
          </Modal>
        </>
      )}
    </React.Fragment>
  );
};

export default ReconciliationMachineFormAccordionContent;
